import { assert } from '../app/logger.js';
import { HTMLControl } from '../controls/html_control.js';
import { FriendListControl } from '../controls/friend_list_control.js';
import { GoingWithDialog } from './goingwith_dialog.js';

/**
 * Going-with control. Either presented as inline list control or a button that
 * opens a sharing dialog.
 */
class GoingWithControl extends HTMLControl {
  static tagName = 'goingwith-control';

  static get selectors() {
    return {
      ListControl: `friend-list-control`,
      ListEditableControl: `friend-list-control ${FriendListControl.selectors.EditableList}`,
      ListValue: `friend-list-control ${FriendListControl.selectors.Value}`,
      InviteLink: `.invite-friends-link`,
    };
  }

  static properties = {
    type: {
      set(v) {
        this.setType(v);
      },
    },
  };

  get control() {
    return this.listControlElement || this.dialog;
  }

  get value() {
    return this.listControlElement.valueAsInvitees;
  }

  set value(vals) {
    assert(this.#friends, `[control:goingwith] no friends given`);

    this.#rawItems = vals || [];
    if (this.linkEl) {
      this.linkEl.hidden = !(this.listControlElement.hidden =
        this.#rawItems.length == 0);
    }
    this.#refresh();
  }

  get readonly() {
    return this.dialog && this.dialog.readonly;
  }
  set readonly(v) {
    if (this.dialog) {
      this.dialog.readonly = v;
    }
  }

  get plainStyle() {
    return undefined;
  }
  set plainStyle(v) {
    // nothing actionalbe so far, because the widget implements styling on its own.
  }

  get friends() {
    return this.#friends;
  }
  set friends(v) {
    if (this.#friends) {
      this.#friends.unpipe(this.#refreshReceiver);
    }
    this.#friends = v;
    if (this.#friends) {
      this.#friends.pipe(this.#refreshReceiver);
    }
  }

  initialize() {
    super.initialize();

    this.classList.add('editable-control-container');

    this.listControlElement.requestItems = term =>
      this.requestItemsFor(this.listControlElement, term);
  }

  static get elements() {
    return {
      listControlElement: this.selectors.ListControl,
    };
  }

  static get markup() {
    return `
<friend-list-control label='Going with'></friend-list-control>
`;
  }

  setType(type) {
    if (this.dialog || type != 'link') {
      return;
    }

    this.dialog = GoingWithDialog.create();
    this.dialog.onaccept = data => this.invitesUpdated(data);
    this.dialog.listControl.requestItems = term =>
      this.requestItemsFor(this.dialog.listControl, term);

    this.listControlElement.readonly = true;
    this.listControlElement.tabable = true;
    this.listControlElement.setAttribute('plain', 'true');
    this.listControlElement.addEventListener(
      'click',
      () => this.dialog.show(),
      true
    );

    this.linkEl = this.ownerDocument.createElement('span');
    this.linkEl.setAttribute('plain', 'true');
    this.linkEl.classList.add('editable-control-container');
    this.linkEl.innerHTML = `
      <span class='editable-control'>
        <span class='link invite-friends-link' tabindex='0'>Invite friends</span>
      </span>
    `;
    this.linkEl.addEventListener('click', () => this.dialog.show());
    this.linkEl.hidden = true;
    this.append(this.linkEl);
  }

  requestItemsFor(listControl, term) {
    if (!this.#friends) {
      return [];
    }

    // Filter out inviteees and emails typed into an list control. In case of
    // dialog's list control, also filter out current items (control's value).
    let invitees = listControl.items;
    if (this.dialog) {
      invitees = invitees.concat(
        this.#items.map(i => ({ email: i.inviteeEmail }))
      );
    }
    invitees = invitees.map(i => i.email || i.name);

    return this.#friends
      .filter(f => f.name.includes(term) && !invitees.includes(f.email))
      .map(f => ({
        id: f.id,
        name: f.name,
        email: f.email,
        img: f.avatar,
      }));
  }

  #refresh() {
    // No update if values are not set yet.
    if (!this.#rawItems || !this.#friends) {
      return;
    }

    this.#items = this.#rawItems.map(v => {
      let friend = this.#friends.find(v);
      return {
        inviteeUserId: friend.id,
        inviteeName: friend.name,
        inviteeAvatar: friend.avatar,
        inviteeEmail: friend.email,
        status: v.status,
        rights: v.rights,
        inviteHash: v.inviteHash,
      };
    });

    this.listControlElement.value = this.#items.map(v => {
      if (v.inviteeUserId) {
        return {
          id: v.inviteeUserId,
          name: v.inviteeName,
          email: v.inviteeEmail,
          title: `${v.inviteeName} <${v.inviteeEmail}>`,
          img: v.inviteeAvatar,
        };
      }
      return {
        name: v.inviteeEmail,
        title: `Incognito <${v.inviteeEmail}>`,
        img: v.inviteeAvatar,
      };
    });

    if (this.dialog) {
      this.dialog.value = this.#items;
    }

    // Value is set when the friend list in the autocomplete are updated.
    this.listControlElement.autocompleteUpdated();
  }

  #friends = null;
  #items = [];
  #rawItems;
  #refreshReceiver = this.#refresh.bind(this);
}

GoingWithControl.defineCustomElement();

export { GoingWithControl };
